import React, { FC } from 'react';
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { offsetToTimeString } from '@pv/common/utils';
import { range } from 'lodash';

import { PVBlack } from '@pv/common/colors';

import type { VoidFn } from '../types/common';

interface TimeSelectorProps {
  label: string;
  timeOffset?: number;
  onChangeTimeOffset: VoidFn<SelectChangeEvent<number>>;
  startOffset?: number;
  endOffset?: number;
}

interface TimeFieldProps {
  startOffset?: number;
  endOffset?: number;
  onChange: ({
    startOffset,
    endOffset,
  }: {
    startOffset?: number;
    endOffset?: number;
  }) => void;
  earliestStartOffset: number;
  latestEndOffset: number;
}

export const TimeField: FC<TimeFieldProps> = ({
  startOffset,
  endOffset,
  onChange,
  earliestStartOffset,
  latestEndOffset,
}: TimeFieldProps) => {
  return (
    <Stack direction="row" gap="16px" width="100%">
      <TimeSelector
        label="Start time"
        timeOffset={startOffset}
        onChangeTimeOffset={(newOffset) => {
          onChange({
            endOffset: endOffset ?? 0,
            startOffset: newOffset.target.value as number,
          });
        }}
        startOffset={earliestStartOffset}
        endOffset={latestEndOffset}
      />
      <TimeSelector
        label="End time"
        timeOffset={endOffset}
        onChangeTimeOffset={(newOffset) => {
          onChange({
            startOffset: startOffset ?? 0,
            endOffset: newOffset.target.value as number,
          });
        }}
        startOffset={startOffset ?? earliestStartOffset}
        endOffset={latestEndOffset}
      />
    </Stack>
  );
};

export const TimeSelector: FC<TimeSelectorProps> = ({
  label,
  timeOffset,
  onChangeTimeOffset,
  startOffset,
  endOffset,
}) => (
  <FormControl fullWidth>
    <InputLabel
      htmlFor={`time-selector-select-${label}`}
      id={`time-selector-${label}`}
    >
      {label}
    </InputLabel>
    <Select
      sx={{
        color: PVBlack,
      }}
      labelId={`time-selector-${label}`}
      id={`time-selector-select-${label}`}
      label={label}
      value={timeOffset ?? ''}
      onChange={onChangeTimeOffset}
    >
      {generateTimeSelection(startOffset, endOffset)}
    </Select>
  </FormControl>
);

const generateTimeSelection = (
  start: number = 60,
  end: number = 1480,
  step: number = 30,
) => {
  const timeOffsets = range(start, end + step, step);
  return timeOffsets.map((timeOffset: number) => {
    const timeString = offsetToTimeString(timeOffset);
    return (
      <MenuItem key={timeString} value={timeOffset}>
        {timeString}
      </MenuItem>
    );
  });
};
